import { TimetableReportType } from '@bp/planung-graphql-types';
import { Button, PdfIcon, Select, SelectOptionType } from '@bp/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayReportType, downloadDisplayReport } from '../../utils/pdfReport';
import { ReportTopic } from './types';

export type ActiveVersionType = {
  uuid: string;
  version: number;
  timetableName: string;
  classUuids: string[];
  roomUuids: string[];
  teacherUuids: string[];
};

type ActiveVersionsReportProps = {
  topic: ReportTopic;
  versions: ActiveVersionType[];
  onLoad: (loading: boolean) => void;
};

const reportTypeByTopic: Partial<Record<ReportTopic, DisplayReportType>> = {
  classes: TimetableReportType.SingleClass,
  rooms: TimetableReportType.SingleRoom,
  teachers: TimetableReportType.SingleTeacher,
};

/**
 * Pläne einer aktiven Version: Version im Dropdown wählen, erzeugen und sofort herunterladen.
 * Anders als die Reports der Stundenplanabschnitte entsteht dabei kein Report-Datensatz, die
 * Datei wird also nicht gelistet (technisch identisch zum "Drucken" an der Pinnwand).
 */
export const ActiveVersionsReport = ({ topic, versions, onLoad }: ActiveVersionsReportProps) => {
  const { t } = useTranslation();

  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const reportType = reportTypeByTopic[topic];
  if (!reportType) return null;

  const options: SelectOptionType[] = versions.map((version) => ({
    value: version.uuid,
    label: `${version.timetableName} – ${t('timetableVersion.title', { count: 1 })} ${version.version}`,
  }));

  // Ohne eigene Auswahl ist die erste Version vorbelegt (die Versionen werden asynchron nachgeladen).
  const selectedVersion = versions.find((version) => version.uuid === selectedUuid) ?? versions[0] ?? null;

  const contextUuids =
    topic === 'rooms'
      ? (selectedVersion?.roomUuids ?? [])
      : topic === 'teachers'
        ? (selectedVersion?.teacherUuids ?? [])
        : (selectedVersion?.classUuids ?? []);

  const contextTranslation =
    topic === 'rooms'
      ? t('rooms.title', { count: 2 })
      : topic === 'teachers'
        ? t('persons.title', { count: 2 })
        : t('classes.title', { count: 2 });

  async function handleCreate() {
    if (loading || !reportType || !selectedVersion || contextUuids.length === 0) return;
    setLoading(true);
    onLoad(true);
    await downloadDisplayReport({
      reportType,
      timetableVersionUuid: selectedVersion.uuid,
      contextUuids,
      downloadFileName: `${selectedVersion.timetableName}_Version_${selectedVersion.version}_${contextTranslation}.pdf`,
    });
    setLoading(false);
    onLoad(false);
  }

  return (
    <div className='px-6 pb-6'>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select
            name='active-version-report'
            options={options}
            value={options.find((option) => option.value === selectedVersion?.uuid) ?? null}
            placeholder={t('timetableVersion.title', { count: 1 })}
            onChange={(event) => {
              const option = event as SelectOptionType | null;
              setSelectedUuid(typeof option?.value === 'string' ? option.value : null);
            }}
            menuPosition='fixed'
          />
        </div>
        <Button
          hierarchy='primary'
          icon={<PdfIcon />}
          isLoading={loading}
          disabled={!selectedVersion || contextUuids.length === 0}
          title={t('common.generate')}
          onClick={handleCreate}
        >
          {t('common.generate')}
        </Button>
      </div>
    </div>
  );
};
